import React, { useEffect, useState, useRef, lazy, Suspense } from 'react';
import { useParams } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import styles from './ClinicPatientContracts.module.scss';
import DashboardTableCard from '../components/DashboardTableCard';
import { BsCheck, BsX } from 'react-icons/bs'
import { FaFilePdf } from 'react-icons/fa';
import { Row, Col } from 'react-bootstrap';
import Loader from '../components/Loader';
import PatientHeader from '../components/PatientHeader';
import Button from '../components/Button';
import ModalForm from '../components/ModalForm';
import ModalShowPdf from '../components/ModalShowPdf';
import ModalCopyLink from '../components/ModalCopyLink';
import { alertModal } from '../utils/modalController';
import { fetchAuth } from '../utils/fetchAuth';

const EditPatientContract = lazy(() => import('../components/EditPatientContract'));
const EditPatientContractPayment = lazy(() => import('../components/EditPatientContractPayment'));
const PaymentStatusModal = lazy(() => import('../components/PaymentStatusModal'));

const apiUrl = process.env.REACT_APP_API_URL || '';

const CheckIcon = BsCheck as React.FC<{ size?: number, color?: string }>;
const NotCheckIcon = BsX as React.FC<{ size?: number, color?: string }>;
const ViewContractIcon = FaFilePdf as React.FC<{ size?: number, color?: string }>;

const ClinicPatientContracts: React.FC = () => {

    const [user, setUser] = useState<User | null>(null);
    const [age, setAge] = useState<number | null>(null);
    const [contracts, setContracts] = useState<ConsultationPackages[]>([]);
    const [selectedContract, setSelectedContract] = useState<ConsultationPackages | null>(null);
    const [contractsData, setContractsData] = useState<Contract[]>([]);
    const [protocols, setProtocols] = useState<Protocolo[]>([]);
    const [showPdf, setShowPdf] = useState(false);
    const [showNewFormModal, setShowNewFormModal] = useState(false);
    const [showCopyPaymentLinkModal, setShowCopyPaymentLinkModal] = useState(false);
    const [showEditPaymentModal, setShowEditPaymentModal] = useState(false);
    const [showPaymentStatusModal, setShowPaymentStatusModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const { id } = useParams<{ id: string }>();

    useEffect(() => {
        Promise.all([
            fetchAuth(`${apiUrl}/consultorio/patients/${id}`),
            fetchAuth(`${apiUrl}/consultorio/patients/${id}/consultation-packages`),
            fetchAuth(`${apiUrl}/consultorio/protocolos`),
            fetchAuth(`${apiUrl}/consultorio/contracts`),
        ])
            .then(async ([patientRes, contractsRes, protocolosRes, contractsDataRes]) => {
                if(!contractsRes.ok || !protocolosRes.ok || !contractsDataRes.ok || !patientRes.ok){
                    throw new Error("Erro ao buscar dados relacionados ao paciente");
                }
                const patientJson = await patientRes.json();
                const contractsJson = await contractsRes.json();
                const protocolosJson = await protocolosRes.json();
                const contractsDataJson = await contractsDataRes.json();

                if(!patientJson.user){
                    throw new Error("Usuário não encontrado");
                }

                const userRes = await fetchAuth(`${apiUrl}/consultorio/users/${patientJson.user.id}`);

                if (!userRes.ok) {
                    const error_json = await userRes.json();
                    throw new Error(error_json.message || "Erro desconhecido");
                }

                const userJson = await userRes.json();

                setUser(userJson);
                setAge(userJson?.birthday ? Math.floor((new Date().getTime() - new Date(userJson.birthday).getTime()) / (1000 * 60 * 60 * 24 * 365)) : null);
                setContracts(contractsJson);
                setProtocols(protocolosJson);
                setContractsData(contractsDataJson);
            })
            .catch((err) => {
                console.error('Erro ao buscar dados:', err);
                setUser(null);
                setContracts([]);
                setAge(null);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [id, showNewFormModal, showEditPaymentModal]);

    if (loading) return <Loader />;

    return (
        <>
            <Container className={styles.container}>
                <h2 className={styles.title}>Consultório</h2>
                {loading ? (
                    <Loader />
                ) : (
                    <>
                        <Row className={styles["row-card"]}>
                            <Col>
                                <PatientHeader
                                    user={user!}
                                    age={age}
                                    breadcrumbItems={[
                                        { label: "Consultório", href: "/consultorio" },
                                        { label: "Pacientes", href: "/consultorio/pacientes" },
                                        { label: user?.name || "Paciente", href: `/consultorio/pacientes/${id}` },
                                        { label: "Contratos" },
                                    ]}
                                />
                            </Col>
                        </Row>
                    </>
                )}
            </Container>
            <DashboardTableCard
                welcomeMessage={`Contratos`}
                tableData={{
                    headings: ['Assinado', 'Data', 'Protocolo', 'Quantidade de Consultas', 'Validade', 'Valor', 'Pagamento', 'Ações'],
                    data: [
                        [<a href='#' className={styles["link"]} onClick={(e) => {
                            e.preventDefault();
                            setShowNewFormModal(true);
                        }}>Novo contrato</a>],
                        ...contracts.map((contract) => [
                            contract.have_contract ? (contract.is_signed ? (<CheckIcon />) : (<NotCheckIcon />)) : (<></>),
                            contract.created_at ? new Date(contract.created_at).toLocaleDateString('pt-BR', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                            }) : '---',
                            contract.title,
                            (contract.number_of_sessions ?? 0) === 0 ? 'Ilimitada' : (contract.number_of_sessions + (contract.sessions_left > 0 ? ` (${contract.sessions_left} restantes)` : '')),
                            (contract.duration ?? 0) === 0 ? 'Vitalício' : (contract.valid_date ? new Date(contract.valid_date).toLocaleDateString('pt-BR', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                            }) : contract.duration + ' dias'),
                            (contract.valor ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
                            <a href='#' onClick={
                                (e) => {
                                    e.preventDefault();
                                    setSelectedContract(contract);
                                    if (contract.paymentStatus !== 'link_created') {
                                        if (contract.paymentForm && contract.paymentForm === 'mercado_pago') {
                                            setShowPaymentStatusModal(true);
                                        } else {
                                            setShowEditPaymentModal(true);
                                        }
                                    } else {
                                        setShowCopyPaymentLinkModal(true);
                                    }
                                }
                            }>{contract.paymentStatus ? (
                                contract.paymentStatus === 'approved' ? 'Aprovado' :
                                    contract.paymentStatus === 'authorized' ? 'Autorizado' :
                                        contract.paymentStatus === 'pending' ? 'Pendente' :
                                            contract.paymentStatus === 'link_created' ? 'Link criado' :
                                                contract.paymentStatus === 'in_process' ? 'Em processamento' :
                                                    contract.paymentStatus === 'in_mediation' ? 'Em mediação' :
                                                        contract.paymentStatus === 'rejected' ? 'Rejeitado' :
                                                            contract.paymentStatus === 'cancelled' ? 'Cancelado' :
                                                                contract.paymentStatus === 'refunded' ? 'Reembolsado' :
                                                                    contract.paymentStatus === 'charged_back' ? 'Estornado' : ''
                            ) : (contract.valor > 0 ? 'Criar' : '')}</a>,
                            contract.have_contract ? <a href='#' onClick={(e) => {
                                e.preventDefault();
                                setSelectedContract(contract);
                                setShowPdf(true);
                            }}><ViewContractIcon /></a> : ''
                        ])
                    ]

                }}
            />
            <ModalShowPdf
                title="Visualizar Contrato"
                pdfUrl={`${apiUrl}/consultorio/documents/contract-${selectedContract?.id}`}
                show={showPdf}
                handleClose={() => setShowPdf(false)}
                footerButtons={[
                    <>
                        <input
                            type="file"
                            accept="application/pdf"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            onChange={() => {
                                const file = fileInputRef.current?.files?.[0];
                                if (!file || !selectedContract) return;

                                setSaving(true);

                                const formData = new FormData();
                                formData.append('file', file);

                                fetchAuth(`${apiUrl}/consultorio/consultation-packages/${selectedContract.id}/send-sign-contract`, {
                                    method: 'POST',
                                    body: formData,
                                })
                                    .then(async (res) => {
                                        if (!res.ok) {
                                            const error_json = await res.json();
                                            throw new Error(error_json.message || "Erro desconhecido");
                                        }
                                        const json = await res.json();

                                        setContracts(contracts.map((contract) =>
                                            contract.id === selectedContract.id ? { ...contract, isSigned: true } : contract
                                        ));

                                        setSelectedContract({ ...selectedContract, is_signed: true });
                                        setShowPdf(false);
                                    })
                                    .catch((err) => {
                                        console.error('Erro ao enviar assinatura:', err);
                                        alertModal('Erro ao enviar assinatura: ' + err);
                                    })
                                    .finally(() => {
                                        setSaving(false);
                                    });
                            }}
                        />
                        <Button
                            className={[styles['btn-modal'], selectedContract?.is_signed ? 'd-none' : ''].join(' ')}
                            onClick={() => fileInputRef.current?.click()}
                            disabled={saving}
                        >
                            {saving ? 'Enviando...' : 'Enviar Assinatura'}
                        </Button>

                    </>,
                    <Button onClick={() => setShowPdf(false)} disabled={saving} className={styles['btn-modal']}>
                        Fechar
                    </Button>
                ]
                }
            />
            <ModalForm
                title="Novo Contrato"
                show={showNewFormModal}
                onHide={() => { setShowNewFormModal(false); setSelectedContract(null); }}
                onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.currentTarget);

                    formData.append('patientId', id!);
                    const formDataJson = Object.fromEntries(formData.entries());
                    setSaving(true);

                    fetchAuth(`${apiUrl}/consultorio/consultation-packages`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(formDataJson),
                    })
                        .then(async (res) => {
                            if (!res.ok) {
                                const error_json = await res.json();
                                throw new Error(error_json.message || "Erro desconhecido");
                            }
                            const json = await res.json();

                            setShowNewFormModal(false);
                        })
                        .catch((err) => {
                            console.error('Erro ao criar contrato:', err);
                            alertModal('Erro ao criar contrato: ' + err);
                        })
                        .finally(() => {
                            setSaving(false);
                        });
                }}
                footerButtons={[
                    <Button type="submit" disabled={saving} className={styles['btn-modal']}>
                        {saving ? 'Salvando...' : 'Salvar'}
                    </Button>,
                    <Button onClick={() => setShowNewFormModal(false)} disabled={saving} className={styles['btn-modal']}>
                        Fechar
                    </Button>
                ]}
            >
                <Suspense fallback={<Loader />}>
                <EditPatientContract
                    protocols={protocols}
                    contracts={contractsData}
                />
                </Suspense>
            </ModalForm>
            <ModalForm
                title="Editar Pagamento"
                show={showEditPaymentModal}
                onHide={() => { setShowEditPaymentModal(false); setSelectedContract(null); }}
                onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.currentTarget);
                    const formDataJson = Object.fromEntries(formData.entries());

                    setSaving(true);

                    fetchAuth(`${apiUrl}/consultorio/consultation-packages/${selectedContract?.id}/payment`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(formDataJson),
                    })
                        .then(async (res) => {
                            if (!res.ok) {
                                const error_json = await res.json();
                                throw new Error(error_json.message || "Erro desconhecido");
                            }
                            const json = await res.json();

                            setShowEditPaymentModal(false);
                        })
                        .catch((err) => {
                            console.error('Erro ao criar contrato:', err);
                            alertModal('Erro ao criar contrato: ' + err);
                        })
                        .finally(() => {
                            setSaving(false);
                        });
                }}
                footerButtons={[
                    <Button type="submit" disabled={saving} className={styles['btn-modal']}>
                        {saving ? 'Salvando...' : 'Salvar'}
                    </Button>,
                    <Button onClick={() => setShowEditPaymentModal(false)} disabled={saving} className={styles['btn-modal']}>
                        Fechar
                    </Button>
                ]}
            >
                <Suspense fallback={<Loader />}>
                <EditPatientContractPayment
                    payment={{
                        id: selectedContract?.id || 0,
                        valor: selectedContract?.valor || 0,
                    }}
                />
                </Suspense>
            </ModalForm>
            <ModalCopyLink
                show={showCopyPaymentLinkModal}
                onClose={() => setShowCopyPaymentLinkModal(false)}
                urlPath={`pagamento/${selectedContract?.id}`}
            />
            <Suspense fallback={<Loader />}>
            <PaymentStatusModal
                payment={{
                    id: selectedContract?.id || 0,
                    valor: selectedContract?.valor || 0
                }}
                show={showPaymentStatusModal}
                onHide={() => {
                    setShowPaymentStatusModal(false);
                    setSelectedContract(null);
                }}
            />
            </Suspense>
        </>
    );
};

export default ClinicPatientContracts;
